import React, { useState, useEffect } from 'react'
import { C, Avatar, LiveDot, Track, Mono, Spinner, Empty } from '../components/ui'

const PLAYS_KEY = 'linebrkr_plays'
function loadPlays() { try { return JSON.parse(localStorage.getItem(PLAYS_KEY) || '[]') } catch { return [] } }
function savePlays(p) {
  localStorage.setItem(PLAYS_KEY, JSON.stringify(p))
  window.dispatchEvent(new Event('storage'))
}

function calcPL(stake, odds, won) {
  if (!stake || !odds) return 0
  const s = parseFloat(stake), o = parseInt(odds)
  if (!won) return -s
  return o > 0 ? s * (o / 100) : s / (Math.abs(o) / 100)
}

// ── Settle Sheet ──────────────────────────────────────────────────────────────
function SettleSheet({ play, onSettle, onClose }) {
  const [result, setResult] = useState('')
  const [stake, setStake] = useState(String(play.stake || ''))

  const settle = (status) => {
    const s = parseFloat(stake) || 0
    const pl = status === 'push' ? 0 : calcPL(s, play.odds, status === 'won')
    onSettle({ ...play, status, stake: s, profitLoss: pl, result, settledAt: new Date().toISOString() })
    onClose()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', background: C.bk2, borderRadius: '16px 16px 0 0', padding: '20px 16px 36px', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 18 }}>Settle: {play.playerName}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>Stake ($)</div>
          <input value={stake} onChange={e => setStake(e.target.value)} placeholder="e.g. 25" style={{ width: '100%', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>Result (optional)</div>
          <input value={result} onChange={e => setResult(e.target.value)} placeholder="e.g. 2-for-4, 1 HR" style={{ width: '100%', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box' }} />
        </div>
        {stake && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {['won', 'lost', 'push'].map(s => {
              const pl = s === 'push' ? 0 : calcPL(parseFloat(stake), play.odds, s === 'won')
              const color = s === 'won' ? C.green : s === 'lost' ? C.red : C.dim
              return (
                <div key={s} style={{ flex: 1, textAlign: 'center', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 4px' }}>
                  <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase' }}>{s}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color, marginTop: 2 }}>{pl >= 0 ? '+' : ''}${Math.abs(pl).toFixed(2)}</div>
                </div>
              )
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => settle('won')} style={{ flex: 1, background: `${C.green}20`, border: `1px solid ${C.green}44`, borderRadius: 10, padding: '12px', color: C.green, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: '.06em', cursor: 'pointer' }}>✅ WON</button>
          <button onClick={() => settle('lost')} style={{ flex: 1, background: `${C.red}20`, border: `1px solid ${C.red}44`, borderRadius: 10, padding: '12px', color: C.red, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: '.06em', cursor: 'pointer' }}>❌ LOST</button>
          <button onClick={() => settle('push')} style={{ flex: 1, background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px', color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: '.06em', cursor: 'pointer' }}>PUSH</button>
        </div>
      </div>
    </div>
  )
}

// ── Add Play Sheet ────────────────────────────────────────────────────────────
function AddPlaySheet({ onAdd, onClose }) {
  const [form, setForm] = useState({ playerName: '', team: '', market: 'HRR_over_1.5', line: '1.5', odds: '', stake: '', confidence: '', edge: '' })
  const handleSubmit = () => {
    if (!form.playerName || !form.odds) return
    onAdd({ id: Date.now(), ...form, odds: parseInt(form.odds), line: parseFloat(form.line), stake: form.stake ? parseFloat(form.stake) : null, confidence: form.confidence ? parseInt(form.confidence) : null, edge: form.edge || null, status: 'upcoming', createdAt: new Date().toISOString(), idx: Math.floor(Math.random() * 6) })
    onClose()
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', background: C.bk2, borderRadius: '16px 16px 0 0', padding: '20px 16px 36px', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 18 }}>Save a Play</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {[
          { key: 'playerName', label: 'Player Name', placeholder: 'e.g. Aaron Judge' },
          { key: 'team', label: 'Team', placeholder: 'e.g. NYY' },
          { key: 'odds', label: 'Odds (American)', placeholder: 'e.g. +145 or -110' },
          { key: 'stake', label: 'Stake ($)', placeholder: 'Optional' },
          { key: 'edge', label: 'Edge %', placeholder: 'Optional' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
            <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} style={{ width: '100%', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box' }} />
          </div>
        ))}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>Market</div>
          <select value={form.market} onChange={e => setForm(p => ({ ...p, market: e.target.value }))} style={{ width: '100%', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, boxSizing: 'border-box' }}>
            <option value="HRR_over_1.5">HRR Over 1.5</option>
            <option value="total_bases">Total Bases</option>
            <option value="HR_anytime">HR Anytime</option>
            <option value="strikeouts">Strikeouts</option>
          </select>
        </div>
        <button onClick={handleSubmit} style={{ width: '100%', background: `linear-gradient(135deg, ${C.blueDim}, ${C.blue})`, border: 'none', borderRadius: 10, padding: '13px', color: '#fff', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: '.06em', cursor: 'pointer' }}>SAVE PLAY</button>
      </div>
    </div>
  )
}

// ── Live Card ─────────────────────────────────────────────────────────────────
function LiveCard({ play, onSettle }) {
  const [open, setOpen] = useState(false)
  const pct = play.current != null && play.line != null ? Math.min(100, Math.round((play.current / play.line) * 100)) : 50
  return (
    <div style={{ background: `linear-gradient(135deg, ${C.bk3}, ${C.bk2})`, border: `1px solid ${C.green}44`, borderRadius: 16, margin: '0 16px 12px', overflow: 'hidden', boxShadow: `0 0 20px ${C.green}10` }}>
      <div style={{ padding: '7px 14px', background: `${C.green}10`, borderBottom: `1px solid ${C.green}22`, display: 'flex', alignItems: 'center', gap: 7 }}>
        <LiveDot /><span style={{ fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.green, letterSpacing: '.08em' }}>LIVE SWEAT</span>
        {play.gameInfo && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim, marginLeft: 4 }}>{play.gameInfo}</span>}
        <button onClick={() => onSettle(play)} style={{ marginLeft: 'auto', background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.blue, letterSpacing: '.05em' }}>SETTLE</button>
      </div>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}>
        <Avatar name={play.playerName} idx={play.idx || 0} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{play.playerName}</div>
          <div style={{ display: 'flex', gap: 5, marginTop: 4, alignItems: 'center' }}>
            <span style={{ background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 5, padding: '2px 7px', fontSize: 9.5, fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: C.blue }}>{play.market}</span>
            <Mono size={10} color={C.dim}>{play.team}</Mono>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: play.odds > 0 ? C.green : C.blue }}>{play.odds > 0 ? '+' : ''}{play.odds}</div>
          {play.stake && <Mono size={9} color={C.dim}>Stake: ${play.stake}</Mono>}
        </div>
      </div>
      <div style={{ padding: '0 14px 14px' }}>
        <Track pct={pct} color={C.blue} height={6} shimmer />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <Mono size={9} color={C.dim}>0</Mono>
          <Mono size={9} color={C.yellow}>Line {play.line}</Mono>
          <Mono size={9} color={C.dim}>↑</Mono>
        </div>
      </div>
    </div>
  )
}

// ── Upcoming Card ─────────────────────────────────────────────────────────────
function UpcomingCard({ play, onSettle, onRemove }) {
  return (
    <div style={{ background: `linear-gradient(135deg, ${C.bk3}, ${C.bk2})`, border: `1px solid ${C.border}`, borderRadius: 16, margin: '0 16px 10px', overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <Avatar name={play.playerName} idx={play.idx || 0} size={42} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{play.playerName}</div>
          <div style={{ display: 'flex', gap: 5, marginTop: 4, alignItems: 'center' }}>
            <span style={{ background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 5, padding: '2px 7px', fontSize: 9.5, fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: C.blue }}>{play.market}</span>
            <Mono size={9} color={C.dim}>{play.team}</Mono>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: C.blue }}>{play.odds > 0 ? '+' : ''}{play.odds}</div>
          {play.edge && <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.green }}>+{play.edge}% edge</div>}
        </div>
      </div>
      {play.confidence > 0 && (
        <div style={{ padding: '0 14px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.08em' }}>HIT CHANCE</span>
          <div style={{ flex: 1, height: 3, background: C.dim2, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${play.confidence}%`, background: `linear-gradient(90deg, ${C.blueDim}, ${C.blue})`, borderRadius: 2 }} />
          </div>
          <Mono size={10} color={C.blue}>{play.confidence}%</Mono>
        </div>
      )}
      <div style={{ padding: '8px 14px 12px', display: 'flex', gap: 6 }}>
        <button onClick={() => onSettle(play)} style={{ flex: 1, background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 8, padding: '7px', cursor: 'pointer', fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.blue, letterSpacing: '.05em' }}>MARK RESULT</button>
        <button onClick={() => onRemove(play.id)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.dim }}>✕</button>
      </div>
    </div>
  )
}

// ── Finished Card ─────────────────────────────────────────────────────────────
function FinishedCard({ play }) {
  const [open, setOpen] = useState(false)
  const won = play.status === 'won'
  const push = play.status === 'push'
  const color = won ? C.green : push ? C.dim : C.red
  return (
    <div onClick={() => setOpen(o => !o)} style={{ background: C.bk2, border: `1px solid ${won ? `${C.green}33` : push ? C.border : `${C.red}22`}`, borderRadius: 14, margin: '0 16px 8px', cursor: 'pointer' }}>
      <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}18`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
          {won ? '✅' : push ? '↔️' : '❌'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{play.playerName}</div>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{play.market} · {play.result || play.team || '—'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color }}>
            {play.profitLoss != null ? `${play.profitLoss >= 0 ? '+' : ''}$${Math.abs(play.profitLoss).toFixed(2)}` : '—'}
          </div>
          {play.edge && <div style={{ fontSize: 9, color: C.dim }}>{play.edge}% edge</div>}
        </div>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${C.dim2}`, padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {[
            { l: 'Odds', v: play.odds ? `${play.odds > 0 ? '+' : ''}${play.odds}` : '—', c: C.blue },
            { l: 'Stake', v: play.stake ? `$${play.stake}` : '—', c: C.text },
            { l: 'Confidence', v: play.confidence ? `${play.confidence}%` : '—', c: C.blue },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MyPlaysTab() {
  const [section,  setSection]  = useState('upcoming')
  const [plays,    setPlays]    = useState(loadPlays)
  const [showAdd,  setShowAdd]  = useState(false)
  const [settling, setSettling] = useState(null)

  // Reload from localStorage whenever tab mounts or storage changes
  useEffect(() => {
    setPlays(loadPlays())
    const sync = () => setPlays(loadPlays())
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])

  const update = (next) => { setPlays(next); savePlays(next) }
  const handleAdd = (play) => update([...plays, play])
  const handleSettle = (settled) => update(plays.map(p => p.id === settled.id ? settled : p))
  const handleRemove = (id) => update(plays.filter(p => p.id !== id))

  const livePlays     = plays.filter(p => p.status === 'live')
  const upcomingPlays = plays.filter(p => p.status === 'upcoming')
  const finishedPlays = plays.filter(p => ['won', 'lost', 'push'].includes(p.status))
  const wins = finishedPlays.filter(p => p.status === 'won').length
  const totalPL = finishedPlays.reduce((s, p) => s + (p.profitLoss || 0), 0)
  const totalStake = finishedPlays.reduce((s, p) => s + (p.stake || 0), 0)
  const roi = totalStake > 0 ? ((totalPL / totalStake) * 100).toFixed(1) : null

  const tabs = [
    { id: 'live',     label: 'Live Sweats', count: livePlays.length },
    { id: 'upcoming', label: 'Open Plays',  count: upcomingPlays.length },
    { id: 'finished', label: 'Results',     count: finishedPlays.length },
  ]

  return (
    <div style={{ paddingBottom: 84 }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: `linear-gradient(180deg, ${C.bk} 85%, transparent)` }}>
        <div style={{ padding: '16px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.blue, fontSize: 20 }}>◆</span>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 22 }}>My Plays</span>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 8, padding: '6px 14px', color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: '.06em', cursor: 'pointer' }}>+ SAVE PLAY</button>
        </div>
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginTop: 12 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setSection(t.id)} style={{ flex: 1, background: 'none', border: 'none', padding: '9px 0 11px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', color: section === t.id ? C.blue : C.dim, borderBottom: `2px solid ${section === t.id ? C.blue : 'transparent'}`, transition: 'all .18s' }}>
              {t.label}{t.count > 0 && <span style={{ marginLeft: 5, background: section === t.id ? C.blueBg : C.bk3, borderRadius: 4, padding: '1px 5px', fontSize: 10 }}>{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Stats strip */}
      {finishedPlays.length > 0 && (
        <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
          {[
            { l: 'Record',    v: `${wins}-${finishedPlays.length - wins}`, c: C.blue },
            { l: 'P/L',       v: `${totalPL >= 0 ? '+' : ''}$${Math.abs(totalPL).toFixed(0)}`, c: totalPL >= 0 ? C.green : C.red },
            { l: 'ROI',       v: roi != null ? `${roi}%` : '—', c: parseFloat(roi) >= 0 ? C.green : C.red },
            { l: 'Win Rate',  v: finishedPlays.length ? `${Math.round((wins / finishedPlays.length) * 100)}%` : '—', c: C.text },
          ].map((s, i) => (
            <div key={i} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 6px', textAlign: 'center' }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 700, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 4 }}>
        {section === 'live' && (livePlays.length === 0 ? <Empty message="No live plays right now" /> : livePlays.map(p => <LiveCard key={p.id} play={p} onSettle={setSettling} />))}
        {section === 'upcoming' && (
          upcomingPlays.length === 0
            ? <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>◆</div>
                <div style={{ fontSize: 14, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: C.dim, marginBottom: 10 }}>No open plays saved</div>
                <button onClick={() => setShowAdd(true)} style={{ background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 10, padding: '10px 20px', color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>+ Save your first play</button>
              </div>
            : upcomingPlays.map(p => <UpcomingCard key={p.id} play={p} onSettle={setSettling} onRemove={handleRemove} />)
        )}
        {section === 'finished' && (finishedPlays.length === 0 ? <Empty message="No finished plays yet" /> : finishedPlays.map(p => <FinishedCard key={p.id} play={p} />))}
      </div>

      {showAdd   && <AddPlaySheet onAdd={handleAdd} onClose={() => setShowAdd(false)} />}
      {settling  && <SettleSheet play={settling} onSettle={handleSettle} onClose={() => setSettling(null)} />}
    </div>
  )
}
